import Box from '@mui/material/Box';
import Radio from '@mui/material/Radio';
import {
  DataGridPro,
  GridApiPro,
  GridCallbackDetails,
  GridGroupNode,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { isEqual } from 'lodash';
import uniqueId from 'lodash.uniqueid';
import React, { Component, SyntheticEvent } from 'react';
import { AppContext } from '../../common/AppContext';
import { translate as i18nTranslate } from '../../i18n/i18nConfig';
import { WithLocaleProps } from '../../providers/LocaleProvider';
import ChevronDownIcon from '../../resources/images/icons-chevron-down.svg';
import ChevronRightIcon from '../../resources/images/icons-chevron-right.svg';
import ChevronUpIcon from '../../resources/images/icons-chevron-up.svg';
import SearchIcon from '../../resources/images/icons-search.svg';
import type {
  BaseTableRow,
  ColumnBounds,
  TableColumnDef,
  TableProps,
  TableState,
} from '../../types/components/table.types';
import { Paper } from '../Paper';
import { SectionLoader } from '../SectionLoader';
import TablePagination from '../TablePagination';
import { TablePlaceholder } from '../TablePlaceholder';

export class Table<
  TRow extends BaseTableRow = BaseTableRow,
  TProps extends TableProps<TRow> & Partial<WithLocaleProps> = TableProps<TRow> &
    Partial<WithLocaleProps>,
  TState extends TableState = TableState,
> extends Component<TProps, TState> {
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  static defaultProps: Partial<TableProps> = {
    columns: [],
    density: 'standard',
    showFooterRowCount: true,
    pagination: true,
    paper: true,
    showHeader: true,
    showHeaderFromBreakpoint: null,
    showFooter: true,
    readOnly: false,
    sortable: true,
    noRowsComponent: null,
    noResultsComponent: null,
    tree: false,
    selectable: true,
    autoHeight: true,
    rowCount: undefined,
    pageSize: 50,
    rowsPerPageOptions: [50],
    paginationMode: 'client',
    filterMode: 'client',
    sortingMode: 'client',
    searchText: '',
    // Callers that don't pass translate would otherwise render raw "tableColumns.*" keys as headers
    translate: i18nTranslate,
  };

  ref: React.RefObject<HTMLDivElement | null>;
  apiRef: React.MutableRefObject<GridApiPro>;
  tableId: string;
  _isMounted!: boolean;
  gridApi: unknown;
  columns: TableColumnDef<TRow>[] = [];

  isSubscribedEvent = false;

  // Bound event handlers - stored as class properties for proper cleanup
  private boundOnWindowResize: (e: Event) => void;
  private boundHandleOutsideClick: (e: MouseEvent) => void;
  private boundHandleEscapeKey: (e: KeyboardEvent) => void;

  constructor(props: TProps) {
    super(props);

    this.state = {
      sortModel: this.props.sortModel,
      selectionModel: [],
      expandedRowIds: props.expandedRowIds || [],
    } as unknown as TState;

    this.ref = React.createRef();
    // Initialize with empty object for DataGridPro PropTypes validation
    // DataGridPro will populate this with the actual API on mount
    this.apiRef = React.createRef() as React.MutableRefObject<GridApiPro>;

    this.tableId = uniqueId('klayo-table_');

    this.initColumns();

    // Pre-bind event handlers for proper cleanup
    this.boundOnWindowResize = this.onWindowResize.bind(this);
    this.boundHandleOutsideClick = this.handleOutsideClick.bind(this);
    this.boundHandleEscapeKey = this.handleEscapeKey.bind(this);
  }

  onWindowResize(_e: Event): void {
    if (this._isMounted) {
      this.forceUpdate();
    }
  }

  componentDidMount(): void {
    this._isMounted = true;

    // Attach event listeners when the component mounts
    window.addEventListener('resize', this.boundOnWindowResize);
    document.addEventListener('click', this.boundHandleOutsideClick);
    document.addEventListener('keydown', this.boundHandleEscapeKey);

    this.subscribeToRowExpansion();
    this.setState({
      rowsPerPageOptions: this.props.rowsPerPageOptions,
    });
  }

  componentDidUpdate(prevProps: TableProps<TRow>, _prevState: TableState): void {
    const { onSortModelChange } = this.props;

    this.subscribeToRowExpansion();

    if (prevProps.sortModel !== this.props.sortModel && onSortModelChange)
      this.setState({ sortModel: this.props.sortModel });
    if (prevProps.columns !== this.props.columns) {
      this.initColumns();
    }
    if (prevProps.rows !== this.props.rows || prevProps.columns !== this.props.columns)
      this.forceUpdate();
  }

  subscribeToRowExpansion(): void {
    // Check for subscribeEvent method to ensure apiRef has been populated by DataGridPro
    if (!this.isSubscribedEvent && this.apiRef.current?.subscribeEvent) {
      this.apiRef.current.subscribeEvent('rowExpansionChange', (node: unknown) => {
        this.handleRowExpansionChange(node as GridGroupNode);
      });
      this.isSubscribedEvent = true;
    }
  }

  handleRowExpansionChange(node: GridGroupNode): void {
    const { onExpandRowChange } = this.props;
    const expandedRowIds = node.childrenExpanded
      ? Array.from(new Set([...this.state.expandedRowIds, node.groupingKey as string]))
      : this.state.expandedRowIds.filter((id) => id !== node.groupingKey);

    this.setState({ expandedRowIds }, () => {
      if (onExpandRowChange) onExpandRowChange(expandedRowIds);
    });
  }

  componentWillUnmount(): void {
    this._isMounted = false;
    // Clean up all event listeners when the component unmounts
    window.removeEventListener('resize', this.boundOnWindowResize);
    document.removeEventListener('click', this.boundHandleOutsideClick);
    document.removeEventListener('keydown', this.boundHandleEscapeKey);

    this.isSubscribedEvent = false;
  }

  handleOutsideClick(event: MouseEvent): void {
    const { disableSelection } = this.props;
    if (disableSelection) return;
    if (this.ref.current && this._isMounted) {
      const clickedInsideDataGrid = this.ref.current.contains(event.target as Node);
      if (!clickedInsideDataGrid) {
        // Clicked outside the DataGrid rows, so clear the focused row
        this.clearFocusRow();
      }
    }
  }

  handleEscapeKey(e: KeyboardEvent): void {
    // Click ESC clear table focus row
    if ((e.key === 'Escape' || e.keyCode === 27) && this._isMounted) {
      this.clearFocusRow();
      this.forceUpdate();
    }
  }

  setSortModel(model: GridSortModel, _details: GridCallbackDetails): void {
    // model is an array contain object with field & sort
    this.setState({ sortModel: model });
  }

  setApiRef(apiRef: React.MutableRefObject<GridApiPro>): void {
    if (!apiRef) return;
    this.apiRef = apiRef;
  }

  initColumns(): void {}

  hasColumn(col: TableColumnDef<TRow>): boolean {
    // Moved to columnVisibilityModel
    const typeToCheck = typeof col.type === 'object' ? (col.type as any)?.field : col.type;
    return this.hasColumnType(typeToCheck);
  }

  hasColumnType(type: string | undefined): boolean {
    return (
      !this.props.columns ||
      this.props.columns.some((c) => {
        const colType = typeof c.type === 'object' ? (c.type as any)?.field : c.type;
        return colType === type;
      })
    );
  }

  onUserClick(e: SyntheticEvent, user: unknown, attr?: unknown): void {
    if (this.props.onUserClick) this.props.onUserClick(e, user, attr);
  }

  getRow(_e: TRow): HTMLElement | null {
    return this.apiRef?.current?.getRowElement?.(this.getRowId(_e)) ?? null;
  }

  getRowId(_e?: TRow): string {
    return uniqueId(this.tableId + '_');
  }

  onRowClick(_params: GridRowParams<TRow>, _e: SyntheticEvent): void {}

  getFocusRow(selectionModel: GridRowSelectionModel, _details?: GridCallbackDetails): void {
    const { disableSelection } = this.props;
    if (disableSelection) return;
    this.setState({ selectionModel });
  }

  clearFocusRow(): void {
    this.setState({ selectionModel: [] });
  }

  onHighlightRow(params: { id: GridRowId; api: unknown }): void {
    const { onHighlightRowComplete } = this.props;

    this.gridApi = params.api;

    if (this.gridApi)
      setTimeout(() => {
        const rowEl = this.apiRef.current?.getRowElement?.(params.id);
        if (rowEl) {
          rowEl.classList.add('highlight');
          const y = rowEl.getBoundingClientRect().top + window.pageYOffset - 200;
          window.scrollTo({ top: y, behavior: 'smooth' });

          setTimeout(() => rowEl.classList.remove('highlight'), 1000);
          if (onHighlightRowComplete) onHighlightRowComplete(params.id);
        }
      }, 500);
  }

  getColumnBoundingClientRect(col: TableColumnDef<TRow>): ColumnBounds | null {
    if (!this.apiRef || !this.apiRef.current) return null;
    const colEl = this.apiRef.current?.getColumnHeaderElement?.(col.field ?? '');
    if (!colEl || !colEl.parentElement) return null;

    const parentBounds = colEl.parentElement.getBoundingClientRect();
    const colBounds = colEl.getBoundingClientRect();

    const relativeBounds: ColumnBounds = {
      top: colBounds.top - parentBounds.top,
      right: colBounds.right - parentBounds.right,
      bottom: colBounds.bottom - parentBounds.bottom,
      left: colBounds.left - parentBounds.left,
      width: colBounds.right - colBounds.left,
      height: colBounds.bottom - colBounds.top,
    };

    return relativeBounds;
  }

  getTreeDataPath(_row: TRow): string[] {
    return [];
  }

  shouldComponentUpdate(prevProps: TableProps<TRow>, prevState: TableState): boolean {
    // props
    const {
      tree,
      rows,
      filterItems,
      highlightRow,
      disabled,
      paginationModel,
      disableSelection,
      keyword,
      isLoading,
    } = this.props;
    if (!disableSelection) return true;
    if (!isEqual(prevProps.rows, rows)) {
      return true;
    }
    if (!isEqual(prevProps.paginationModel, paginationModel)) {
      return true;
    }
    if (!isEqual(prevProps.tree, tree)) {
      return true;
    }
    if (!isEqual(prevProps.highlightRow, highlightRow)) {
      return disableSelection ? false : true;
    }
    if (!isEqual(prevProps.disabled, disabled)) {
      return true;
    }
    if (!isEqual(prevProps.filterItems, filterItems)) {
      return true;
    }
    if (!isEqual(prevProps.keyword, keyword)) {
      return true;
    }
    if (!isEqual(prevProps.isLoading, isLoading)) {
      return true;
    }
    //  state
    const { sortModel, selectionModel } = this.state;
    if (!isEqual(prevState.sortModel, sortModel)) {
      return true;
    }
    if (!isEqual(prevState.selectionModel, selectionModel)) {
      return disableSelection ? false : true;
    }

    return false;
  }

  renderTable(): React.ReactNode {
    const {
      tree,
      rows,
      autoHeight,
      totalCount,
      pageSize,
      paginationMode,
      rowsPerPageOptions,
      onSortModelChange,
      selectable,
      density,
      minHeight,
      pagination,
      showFooterRowCount,
      noRowsMessage,
      noRowsComponent,
      noResultsMessage,
      noResultsComponent,
      showFooter,
      filterItems,
      filterMode,
      filterOperator,
      rowHasSelect,
      theme,
      tableSx,
      dense,
      showHeaderFromBreakpoint,
      onPaginationModelChange,
      paginationModel,
      icon,
      searchText,
      sortingMode,
      getTreeDataPath,
      groupingColDef,
      isLoading,
      checkboxSelection,
      onRowSelectionModelChange,
      rowSelectionModel,
      isRowSelectable = () => true,
      isPagination = false,
      isSingleSelection,
      slots,
      translate,
    } = this.props;
    const { sortModel, selectionModel } = this.state;

    // Use translated messages if translate function is available
    const effectiveNoRowsMessage = noRowsMessage || translate?.('common.noRows') || 'No rows';
    const effectiveNoResultsMessage =
      noResultsMessage || translate?.('common.noResults') || 'No results';

    let numRows = 1;

    const columnVisibilityModel: Record<string, boolean> = {};
    this.columns.forEach((c) => {
      if (c.field) {
        columnVisibilityModel[c.field] = c.visibleFromBreakpoint
          ? (theme?.isBreakpointUp(c.visibleFromBreakpoint) ?? true)
          : c.visibleToBreakpoint
            ? (theme?.isBreakpointDown(c.visibleToBreakpoint) ?? true)
            : true;
        if (columnVisibilityModel[c.field] && c.numRows && c.numRows > numRows) numRows = c.numRows;
      }
    });

    const rowHeight = this.props.rowHeight
      ? this.props.rowHeight
      : numRows * 24 + (dense ? 32 : 42);

    let showHeader = this.props.showHeader !== false;
    if (showHeaderFromBreakpoint && theme)
      showHeader = theme.isBreakpointUp(showHeaderFromBreakpoint);

    const tableStyle: Record<string, any> = {
      '& .MuiDataGrid-row': { cursor: selectable ? 'pointer' : 'default' },
      '& .MuiDataGrid-cell': {
        display: 'flex',
        alignItems: 'center',
        lineHeight: 'unset',
      },
      ...tableSx,
    };

    if (minHeight) tableStyle['minHeight'] = minHeight;

    if (!rows) {
      return <SectionLoader />;
    }
    if (searchText && rows && !rows.length)
      return (
        noResultsComponent || (
          <TablePlaceholder text={effectiveNoResultsMessage} icon={icon ?? <SearchIcon />} />
        )
      );
    if (rows && !rows.length && !isLoading)
      return (
        noRowsComponent || (
          <TablePlaceholder text={effectiveNoRowsMessage} icon={icon ?? <SearchIcon />} />
        )
      );

    return (
      <DataGridPro
        className={
          'klayo-table_grid' +
          (theme ? ' klayo-table--breakpoint-' + (theme.getCurrentBreakpoint()?.[0] ?? '') : '')
        }
        isRowSelectable={isRowSelectable}
        pageSizeOptions={rowsPerPageOptions}
        ref={this.ref}
        apiRef={this.apiRef}
        // disabled={disabled}
        groupingColDef={
          groupingColDef ??
          ({
            headerName: '',
            field: 'job',
            width: 34,
            minWidth: 34,
            cellClassName: 'klayo-table_expandbuttoncell',
            valueGetter: () => '',
          } as any)
        }
        treeData={tree}
        getRowClassName={(params) => {
          const rowNode = this.apiRef.current?.getRowNode?.(params.id);
          const isGroupNode = rowNode?.type === 'group';
          const groupNode = isGroupNode ? (rowNode as GridGroupNode) : null;

          if (params.row.isParent && groupNode?.children) {
            let lastChild: HTMLElement | null = null;
            let lastChildIndex = -1;
            groupNode.children.forEach((c: GridRowId) => {
              const childRowEl = this.apiRef.current?.getRowElement?.(c);
              const rowIndex = this.apiRef.current?.getRowIndexRelativeToVisibleRows?.(c) ?? -1;

              if (rowIndex > lastChildIndex) {
                lastChild = childRowEl ?? null;
                lastChildIndex = rowIndex;
              }
            });

            if (lastChild) {
              (lastChild as HTMLElement).classList.add('klayo-table_childrow--lastchild');
            }
          }

          return (
            (params.row.isParent
              ? 'klayo-table_parentrow' +
                (groupNode?.childrenExpanded ? ' klayo-table_parentrow--expanded' : '')
              : params.row.isChild
                ? 'klayo-table_childrow'
                : 'klayo-table_row') +
            (params.isLastVisible ? ' klayo-table_row--lastrow' : '') +
            (params.isFirstVisible ? ' klayo-table_row--firstrow' : '')
          );
        }}
        rows={rows ? rows : []}
        rowCount={paginationMode === 'server' ? (totalCount ?? 0) : undefined}
        density={density}
        columns={this.columns as any}
        columnHeaderHeight={showHeader !== false ? 56 : 0}
        getRowId={this.getRowId.bind(this)}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: pageSize,
            },
          },
        }}
        rowHeight={rowHeight}
        pagination={pagination}
        paginationMode={paginationMode}
        disableColumnMenu={true}
        disableColumnFilter={true}
        hideFooterRowCount={!showFooterRowCount}
        hideFooter={!showFooter}
        disableMultipleRowSelection={!checkboxSelection}
        disableRowSelectionOnClick={!selectable}
        sortModel={sortModel ? sortModel : undefined}
        paginationModel={paginationModel ? paginationModel : undefined}
        onPaginationModelChange={onPaginationModelChange ? onPaginationModelChange : undefined}
        rowSelectionModel={rowSelectionModel ? rowSelectionModel : selectionModel}
        onRowSelectionModelChange={
          onRowSelectionModelChange ? onRowSelectionModelChange : this.getFocusRow.bind(this)
        }
        onRowClick={(params, e) =>
          !rowHasSelect || rowHasSelect(params.row) ? this.onRowClick(params, e) : null
        }
        // onPageChange={(page: any) =>
        //   paginationMode === "server" ? onPageChange(page, pageSize) : {}
        // }
        // onPageSizeChange={(pageSize: any) => onPageSizeChange(pageSize, paginationMode)}
        onSortModelChange={onSortModelChange ? onSortModelChange : this.setSortModel.bind(this)}
        sortingOrder={['desc', 'asc']}
        sortingMode={sortingMode}
        autoHeight={autoHeight}
        getTreeDataPath={getTreeDataPath ? getTreeDataPath : this.getTreeDataPath.bind(this)}
        sx={tableStyle}
        filterMode={filterMode}
        filterModel={{
          items: filterItems ?? [],
          logicOperator: (filterOperator || 'or') as any,
        }}
        columnVisibilityModel={columnVisibilityModel}
        slots={
          {
            noRowsOverlay: (() =>
              noRowsComponent || (
                <TablePlaceholder text={effectiveNoRowsMessage} />
              )) as React.JSXElementConstructor<unknown>,
            noResultsOverlay: (() =>
              noResultsComponent || (
                <TablePlaceholder text={effectiveNoResultsMessage} icon={<SearchIcon />} />
              )) as React.JSXElementConstructor<unknown>,
            treeDataCollapseIcon: theme?.isBreakpointDown('md') ? ChevronUpIcon : ChevronDownIcon,
            treeDataExpandIcon: theme?.isBreakpointDown('md') ? ChevronDownIcon : ChevronRightIcon,
            ...(pagination && {
              pagination: TablePagination,
            }),
            ...(isSingleSelection && {
              baseCheckbox: (props: {
                checked?: boolean;
                onChange?: React.ChangeEventHandler<HTMLInputElement>;
              }) => {
                return <Radio checked={props.checked} onChange={props.onChange} />;
              },
            }),
            ...slots,
          } as React.ComponentProps<typeof DataGridPro>['slots']
        }
        isGroupExpandedByDefault={(node) => {
          return this.state.expandedRowIds?.includes(node.groupingKey as string);
        }}
        loading={isLoading}
        checkboxSelection={checkboxSelection}
        slotProps={{
          pagination: {
            ...(isPagination && { rowsPerPageOptions: rowsPerPageOptions }),
          },
        }}
        localeText={{
          footerRowSelected: (count) =>
            count === 1
              ? (this.props.translate?.('pagination.rowSelected', { count }) ??
                `${count} row selected`)
              : (this.props.translate?.('pagination.rowsSelected', { count }) ??
                `${count} rows selected`),
        }}
      />
    );
  }

  render() {
    const {
      sx,
      tree,
      theme,
      paper,
      title,
      hideFirstLastBorder,
      showHeaderFromBreakpoint,
      titleRightSide,
    } = this.props;

    let showHeader = this.props.showHeader !== false;
    if (showHeaderFromBreakpoint && theme)
      showHeader = theme.isBreakpointUp(showHeaderFromBreakpoint);

    const tableClass =
      'klayo-table' +
      (showHeader !== false ? ' klayo-table--withheader' : '') +
      (hideFirstLastBorder ? ' klayo-table--hidefirstlastborder' : '') +
      (tree ? ' klayo-table--tree' : '');

    return paper ? (
      <Paper
        className={tableClass}
        title={title}
        titleRightSide={titleRightSide}
        sx={{ padding: '10px 16px 23px 16px', ...sx }}
      >
        {this.renderTable()}
      </Paper>
    ) : (
      <Box className={tableClass} sx={sx}>
        {this.renderTable()}
      </Box>
    );
  }
}
